import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { customerService } from '../services/api';

const ConversationContext = createContext();

export const useConversation = () => {
  const context = useContext(ConversationContext);
  if (!context) {
    throw new Error('useConversation must be used within a ConversationProvider');
  }
  return context;
};

const PAGE_SIZE = 1000;

export const ConversationProvider = ({ children }) => {
  const [conversation, setConversation] = useState(null);
  const [latestMessages, setLatestMessages] = useState([]);
  const [olderPages, setOlderPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);

  const currentIdRef = useRef(null);
  const oldestLoadedPageRef = useRef(null);

  const load = useCallback(async (conversationId) => {
    currentIdRef.current = conversationId;
    setLoading(true);
    setOlderPages([]);
    oldestLoadedPageRef.current = null;

    try {
      const [convResponse, countRes] = await Promise.all([
        customerService.getConversation(conversationId),
        customerService.getMessages(conversationId, {
          paginationModel: { page: 0, pageSize: PAGE_SIZE },
        }),
      ]);

      setConversation(convResponse.data);

      const count = countRes.data.count;
      const totalPages = Math.ceil(count / PAGE_SIZE);
      const lastPageNum = Math.max(0, totalPages - 1);

      if (lastPageNum === 0) {
        setLatestMessages(countRes.data.results);
        oldestLoadedPageRef.current = 0;
        setHasOlderMessages(false);
      } else {
        const lastRes = await customerService.getMessages(conversationId, {
          paginationModel: { page: lastPageNum, pageSize: PAGE_SIZE },
        });
        setLatestMessages(lastRes.data.results);
        oldestLoadedPageRef.current = lastPageNum;
        setHasOlderMessages(!!lastRes.data.previous);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOlderMessages = useCallback(async () => {
    const id = currentIdRef.current;
    const oldestPage = oldestLoadedPageRef.current;
    if (loadingOlder || !hasOlderMessages || oldestPage === null || oldestPage <= 0) return;

    setLoadingOlder(true);
    try {
      const prevPageNum = oldestPage - 1;
      const response = await customerService.getMessages(id, {
        paginationModel: { page: prevPageNum, pageSize: PAGE_SIZE },
      });
      setOlderPages(prev => [response.data.results, ...prev]);
      oldestLoadedPageRef.current = prevPageNum;
      setHasOlderMessages(!!response.data.previous);
    } catch (error) {
      console.error('Error loading older messages:', error);
    } finally {
      setLoadingOlder(false);
    }
  }, [loadingOlder, hasOlderMessages]);

  const reload = useCallback(() => {
    if (currentIdRef.current) {
      return load(currentIdRef.current);
    }
  }, [load]);

  const reloadMessages = useCallback(async () => {
    const id = currentIdRef.current;
    if (!id) return;

    try {
      const countRes = await customerService.getMessages(id, {
        paginationModel: { page: 0, pageSize: PAGE_SIZE },
      });
      const count = countRes.data.count;
      const totalPages = Math.ceil(count / PAGE_SIZE);
      const lastPageNum = Math.max(0, totalPages - 1);

      if (lastPageNum === 0) {
        setLatestMessages(countRes.data.results);
      } else {
        const lastRes = await customerService.getMessages(id, {
          paginationModel: { page: lastPageNum, pageSize: PAGE_SIZE },
        });
        setLatestMessages(lastRes.data.results);
      }
    } catch (error) {
      console.error('Error reloading messages:', error);
    }
  }, []);

  const pollControllerRef = useRef(null);

  const poll = useCallback(async () => {
    const id = currentIdRef.current;
    if (!id) return;

    if (pollControllerRef.current) {
      pollControllerRef.current.abort();
    }
    const controller = new AbortController();
    pollControllerRef.current = controller;

    try {
      const config = { signal: controller.signal, timeout: 5000 };

      // Poll mensagens + conversa em paralelo, mas só atualiza conversa via merge
      // para não causar flash no header (o contact/display_name já está carregado)
      const [convResponse, countRes] = await Promise.all([
        customerService.getConversation(id, config),
        customerService.getMessages(id, {
          paginationModel: { page: 0, pageSize: PAGE_SIZE },
        }, config),
      ]);

      // Merge: atualiza só campos de autorização/status, preserva contact para evitar flash
      setConversation(prev => {
        if (!prev || prev.uuid !== convResponse.data.uuid) return convResponse.data;
        return { ...convResponse.data, contact: prev.contact };
      });

      const count = countRes.data.count;
      const totalPages = Math.ceil(count / PAGE_SIZE);
      const lastPageNum = Math.max(0, totalPages - 1);

      if (lastPageNum === 0) {
        setLatestMessages(countRes.data.results);
      } else {
        const lastRes = await customerService.getMessages(id, {
          paginationModel: { page: lastPageNum, pageSize: PAGE_SIZE },
        }, config);
        setLatestMessages(lastRes.data.results);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.code !== 'ERR_CANCELED') {
        console.error('Error polling conversation:', error);
      }
    }
  }, []);

  const value = {
    conversation,
    latestMessages,
    olderPages,
    loading,
    loadingOlder,
    hasOlderMessages,
    load,
    loadOlderMessages,
    reload,
    reloadMessages,
    poll,
  };

  return (
    <ConversationContext.Provider value={value}>
      {children}
    </ConversationContext.Provider>
  );
};
